import { FeedbackNotification } from '@ori-ui/components';
import { CheckHeavy, ErrorCircle } from '@ori-ui/icons';
import { styled } from '@ori-ui/mui';
import type { FC } from 'react';
import { useState, useCallback } from 'react';

import { StepperWithContent, PreStep, WelcomeStep, VerificationStep, OriflameLogo } from '../components';
import { useConfiguration, useNotificationData, useRegistrationStatus } from '../contexts';
import { MessageType } from '../interfaces';
import { useRegistrationData } from '../models/api';

const RootContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  paddingInline: theme.spacing(6),
  [theme.breakpoints.down('sm')]: {
    paddingInline: theme.spacing(3),
  },
}));

const threeSteps = ['PersonalInfoStep', 'SponsorSelectionStep', 'LegalInformationStep'];
const twoSteps = ['PersonalInfoStep', 'LegalInformationStep'];

export const LandingPage: FC = () => {
  const {
    context: { notificationData },
  } = useNotificationData();
  const { setNotificationData, data } = notificationData;
  const { finished } = useRegistrationStatus();
  const [isVerified, setIsVerified] = useState(false);

  const { data: registrationData } = useRegistrationData();

  const feedBackIcon = data?.type === MessageType.error ? <ErrorCircle /> : <CheckHeavy />;

  const { registrationType, registrationId, referral } = useConfiguration();

  const handleClose = useCallback(() => {
    setNotificationData({ isOpen: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps --- not needed updated on other property
  }, [notificationData]);

  const isVipCustomerType = registrationType === 'VipCustomer';
  const isCorrectReferral = referral?.ReferralType === 'Pbs' || referral?.ReferralType === 'ReferralLink';

  const vipWithoutSponsor = isVipCustomerType && !isCorrectReferral;
  const currentSteps = vipWithoutSponsor ? twoSteps : threeSteps;

  const getCorrectContent = () => {
    if (!registrationId) {
      return <PreStep />;
    }
    if (registrationData?.currentStep === 'ContactVerification' && !isVerified) {
      return <VerificationStep handleVerifySuccess={() => setIsVerified(true)} />;
    }
    if (finished || registrationData?.currentStep === 'SubmitStep') {
      return <WelcomeStep />;
    }
    if (registrationData?.registrationId) {
      return (
        <StepperWithContent
          currentStep={registrationData.currentStep}
          steps={currentSteps}
        />
      );
    }

    return null;
  };

  const correctContent = getCorrectContent();

  return (
    <RootContainer>
      {registrationId ? <OriflameLogo /> : null}
      {correctContent}
      <FeedbackNotification
        open={Boolean(data?.isOpen)}
        color={data?.type}
        icon={data?.customIcon ?? feedBackIcon}
        onClose={handleClose}
      >
        <span
          data-key={data?.messageId}
          className="translate"
        >
          {data?.message}
        </span>
      </FeedbackNotification>
    </RootContainer>
  );
};
